import { useEffect, useState } from 'react';
import { fetchSettings, adminUpdateSettings } from '../../lib/db';
import type { SiteSettings } from '../../lib/types';
import { Loader2, Save, CheckCircle2, AlertCircle, Plus, Trash2 } from 'lucide-react';
import { notifySiteSettingsUpdated } from '../../lib/site-settings';
import { getDefaultAboutPageContent, stringifyAboutPageContent } from '../../lib/about-page-content';
import { getDefaultHomePageContent, stringifyHomePageContent } from '../../lib/homepage-content';

const FIELDS = [
  { key: 'site_name', label: 'Site Name', type: 'text' },
  { key: 'organization_name', label: 'Organization Name', type: 'text' },
  { key: 'tagline', label: 'Tagline', type: 'text' },
  { key: 'hero_title', label: 'Hero Title', type: 'text' },
  { key: 'hero_subtitle', label: 'Hero Subtitle', type: 'text' },
  { key: 'contact_email', label: 'Contact Email', type: 'email' },
  { key: 'contact_phone', label: 'Contact Phone', type: 'tel' },
  { key: 'whatsapp_number', label: 'WhatsApp Number', type: 'text' },
  { key: 'contact_address', label: 'Contact Address', type: 'text' },
  { key: 'facebook_url', label: 'Facebook URL', type: 'url' },
  { key: 'twitter_url', label: 'Twitter URL', type: 'url' },
  { key: 'instagram_url', label: 'Instagram URL', type: 'url' },
  { key: 'linkedin_url', label: 'LinkedIn URL', type: 'url' },
  { key: 'youtube_url', label: 'YouTube URL', type: 'url' },
  { key: 'hero_image_url', label: 'Hero Background Image URL', type: 'url' },
  { key: 'about_highlight', label: 'About Highlight', type: 'text' },
  { key: 'empowering_title', label: 'Empowering Women Page Title', type: 'text' },
  { key: 'empowering_subtitle', label: 'Empowering Women Page Subtitle', type: 'text' },
  { key: 'empowering_about', label: 'Empowering Women Page About Text', type: 'text' },
  { key: 'empowering_cta_text', label: 'Empowering Women CTA Text', type: 'text' },
  { key: 'empowering_cta_link', label: 'Empowering Women CTA Link', type: 'url' },
  { key: 'news_heading', label: 'News Section Heading', type: 'text' },
  { key: 'news_subheading', label: 'News Section Intro', type: 'text' },
  { key: 'stat_1_value', label: 'Stat 1 Value', type: 'text' },
  { key: 'stat_1_label', label: 'Stat 1 Label', type: 'text' },
  { key: 'stat_2_value', label: 'Stat 2 Value', type: 'text' },
  { key: 'stat_2_label', label: 'Stat 2 Label', type: 'text' },
  { key: 'stat_3_value', label: 'Stat 3 Value', type: 'text' },
  { key: 'stat_3_label', label: 'Stat 3 Label', type: 'text' },
  { key: 'stat_4_value', label: 'Stat 4 Value', type: 'text' },
  { key: 'stat_4_label', label: 'Stat 4 Label', type: 'text' },
];

export default function AdminSettings() {
  const [settings, setSettings] = useState<SiteSettings>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [aboutContent, setAboutContent] = useState(getDefaultAboutPageContent());
  const [homeContent, setHomeContent] = useState(getDefaultHomePageContent());

  useEffect(() => {
    fetchSettings()
      .then((loadedSettings) => {
        setSettings(loadedSettings);
        const parsedAbout = loadedSettings.about_page_content;
        if (parsedAbout) {
          try {
            setAboutContent(JSON.parse(parsedAbout));
          } catch {
            setAboutContent(getDefaultAboutPageContent());
          }
        }

        const parsedHome = loadedSettings.home_page_content;
        if (parsedHome) {
          try {
            setHomeContent(JSON.parse(parsedHome));
          } catch {
            setHomeContent(getDefaultHomePageContent());
          }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setStatus('idle');
    try {
      const payload = {
        ...settings,
        about_page_content: stringifyAboutPageContent(aboutContent),
        home_page_content: stringifyHomePageContent(homeContent),
      };
      await adminUpdateSettings(payload);
      notifySiteSettingsUpdated(payload);
      setStatus('success');
      setTimeout(() => setStatus('idle'), 3000);
    } catch {
      setStatus('error');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-brand-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h2 className="font-display text-2xl font-bold text-stone-900">Settings</h2>
        <p className="text-stone-500 mt-1">Configure your site's information and contact details.</p>
      </div>

      <form onSubmit={handleSave} className="card p-6 space-y-5">
        {status === 'success' && (
          <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-sm text-green-700 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4" />
            Settings saved successfully.
          </div>
        )}
        {status === 'error' && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700 flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            Failed to save settings. Please try again.
          </div>
        )}

        <div className="space-y-6">
          <div>
            <h3 className="font-semibold text-stone-900">Website Content</h3>
            <p className="text-sm text-stone-500">Update the main site text, hero section, homepage stats, and the About page content.</p>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {FIELDS.map((field) => (
              <div key={field.key} className="md:col-span-2">
                <label htmlFor={field.key} className="label">{field.label}</label>
                <input
                  id={field.key}
                  type={field.type}
                  value={settings[field.key] || ''}
                  onChange={(e) => setSettings({ ...settings, [field.key]: e.target.value })}
                  className="input"
                />
              </div>
            ))}
          </div>

          <div className="rounded-xl border border-stone-200 p-5 space-y-6">
            <div>
              <h4 className="font-semibold text-stone-900">Homepage Content</h4>
              <p className="text-sm text-stone-500">Customize the hero, stats, focus areas, testimonials, news section, and CTA on the homepage.</p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="label">Hero Badge</label>
                <input
                  value={homeContent.hero.badge}
                  onChange={(e) => setHomeContent({ ...homeContent, hero: { ...homeContent.hero, badge: e.target.value } })}
                  className="input"
                />
              </div>
              <div className="md:col-span-2">
                <label className="label">Hero Title</label>
                <textarea
                  value={homeContent.hero.title}
                  onChange={(e) => setHomeContent({ ...homeContent, hero: { ...homeContent.hero, title: e.target.value } })}
                  className="input min-h-[90px]"
                />
              </div>
              <div className="md:col-span-2">
                <label className="label">Hero Subtitle</label>
                <textarea
                  value={homeContent.hero.subtitle}
                  onChange={(e) => setHomeContent({ ...homeContent, hero: { ...homeContent.hero, subtitle: e.target.value } })}
                  className="input min-h-[90px]"
                />
              </div>
              <div className="md:col-span-2">
                <label className="label">Hero Image URL</label>
                <input
                  value={homeContent.hero.imageUrl}
                  onChange={(e) => setHomeContent({ ...homeContent, hero: { ...homeContent.hero, imageUrl: e.target.value } })}
                  className="input"
                />
              </div>
              <div>
                <label className="label">Primary CTA Text</label>
                <input
                  value={homeContent.hero.primaryCtaText}
                  onChange={(e) => setHomeContent({ ...homeContent, hero: { ...homeContent.hero, primaryCtaText: e.target.value } })}
                  className="input"
                />
              </div>
              <div>
                <label className="label">Primary CTA Link</label>
                <input
                  value={homeContent.hero.primaryCtaLink}
                  onChange={(e) => setHomeContent({ ...homeContent, hero: { ...homeContent.hero, primaryCtaLink: e.target.value } })}
                  className="input"
                />
              </div>
              <div>
                <label className="label">Secondary CTA Text</label>
                <input
                  value={homeContent.hero.secondaryCtaText}
                  onChange={(e) => setHomeContent({ ...homeContent, hero: { ...homeContent.hero, secondaryCtaText: e.target.value } })}
                  className="input"
                />
              </div>
              <div>
                <label className="label">Secondary CTA Link</label>
                <input
                  value={homeContent.hero.secondaryCtaLink}
                  onChange={(e) => setHomeContent({ ...homeContent, hero: { ...homeContent.hero, secondaryCtaLink: e.target.value } })}
                  className="input"
                />
              </div>
              <div className="md:col-span-2">
                <label className="label">Hero Eyebrow Items (comma separated)</label>
                <input
                  value={homeContent.hero.eyebrowItems.join(', ')}
                  onChange={(e) => setHomeContent({ ...homeContent, hero: { ...homeContent.hero, eyebrowItems: e.target.value.split(',').map((item) => item.trim()).filter(Boolean) } })}
                  className="input"
                />
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <h5 className="font-semibold text-stone-900">Homepage Stats</h5>
                <p className="text-sm text-stone-500">Edit the metrics shown in the homepage stats strip.</p>
              </div>
              {homeContent.stats.map((stat, index) => (
                <div key={`${stat.label}-${index}`} className="rounded-lg border border-stone-200 p-4 space-y-3">
                  <div className="grid gap-3 md:grid-cols-2">
                    <input
                      value={stat.value}
                      onChange={(e) => {
                        const next = [...homeContent.stats];
                        next[index] = { ...next[index], value: e.target.value };
                        setHomeContent({ ...homeContent, stats: next });
                      }}
                      placeholder="Value"
                      className="input"
                    />
                    <input
                      value={stat.label}
                      onChange={(e) => {
                        const next = [...homeContent.stats];
                        next[index] = { ...next[index], label: e.target.value };
                        setHomeContent({ ...homeContent, stats: next });
                      }}
                      placeholder="Label"
                      className="input"
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="label">Focus Areas Heading</label>
                <input
                  value={homeContent.focusAreasHeading}
                  onChange={(e) => setHomeContent({ ...homeContent, focusAreasHeading: e.target.value })}
                  className="input"
                />
              </div>
              <div className="md:col-span-2">
                <label className="label">Focus Areas Subtitle</label>
                <textarea
                  value={homeContent.focusAreasSubtitle}
                  onChange={(e) => setHomeContent({ ...homeContent, focusAreasSubtitle: e.target.value })}
                  className="input min-h-[80px]"
                />
              </div>
            </div>

            <div className="space-y-4">
              <h5 className="font-semibold text-stone-900">Focus Areas</h5>
              {homeContent.focusAreas.map((area, index) => (
                <div key={`${area.title}-${index}`} className="rounded-lg border border-stone-200 p-4 space-y-3">
                  <input
                    value={area.title}
                    onChange={(e) => {
                      const next = [...homeContent.focusAreas];
                      next[index] = { ...next[index], title: e.target.value };
                      setHomeContent({ ...homeContent, focusAreas: next });
                    }}
                    placeholder="Title"
                    className="input"
                  />
                  <textarea
                    value={area.description}
                    onChange={(e) => {
                      const next = [...homeContent.focusAreas];
                      next[index] = { ...next[index], description: e.target.value };
                      setHomeContent({ ...homeContent, focusAreas: next });
                    }}
                    placeholder="Description"
                    className="input min-h-[80px]"
                  />
                  <input
                    value={area.imageUrl}
                    onChange={(e) => {
                      const next = [...homeContent.focusAreas];
                      next[index] = { ...next[index], imageUrl: e.target.value };
                      setHomeContent({ ...homeContent, focusAreas: next });
                    }}
                    placeholder="Image URL"
                    className="input"
                  />
                </div>
              ))}
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="label">Testimonials Heading</label>
                <input
                  value={homeContent.testimonialsHeading}
                  onChange={(e) => setHomeContent({ ...homeContent, testimonialsHeading: e.target.value })}
                  className="input"
                />
              </div>
              <div className="md:col-span-2">
                <label className="label">Testimonials Subtitle</label>
                <textarea
                  value={homeContent.testimonialsSubtitle}
                  onChange={(e) => setHomeContent({ ...homeContent, testimonialsSubtitle: e.target.value })}
                  className="input min-h-[80px]"
                />
              </div>
            </div>

            <div className="space-y-4">
              <h5 className="font-semibold text-stone-900">Testimonials</h5>
              {homeContent.testimonials.map((testimonial, index) => (
                <div key={`${testimonial.name}-${index}`} className="rounded-lg border border-stone-200 p-4 space-y-3">
                  <input
                    value={testimonial.name}
                    onChange={(e) => {
                      const next = [...homeContent.testimonials];
                      next[index] = { ...next[index], name: e.target.value };
                      setHomeContent({ ...homeContent, testimonials: next });
                    }}
                    placeholder="Name"
                    className="input"
                  />
                  <input
                    value={testimonial.role}
                    onChange={(e) => {
                      const next = [...homeContent.testimonials];
                      next[index] = { ...next[index], role: e.target.value };
                      setHomeContent({ ...homeContent, testimonials: next });
                    }}
                    placeholder="Role"
                    className="input"
                  />
                  <textarea
                    value={testimonial.quote}
                    onChange={(e) => {
                      const next = [...homeContent.testimonials];
                      next[index] = { ...next[index], quote: e.target.value };
                      setHomeContent({ ...homeContent, testimonials: next });
                    }}
                    placeholder="Quote"
                    className="input min-h-[90px]"
                  />
                </div>
              ))}
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="label">News Heading</label>
                <input
                  value={homeContent.newsHeading}
                  onChange={(e) => setHomeContent({ ...homeContent, newsHeading: e.target.value })}
                  className="input"
                />
              </div>
              <div className="md:col-span-2">
                <label className="label">News Subheading</label>
                <textarea
                  value={homeContent.newsSubheading}
                  onChange={(e) => setHomeContent({ ...homeContent, newsSubheading: e.target.value })}
                  className="input min-h-[80px]"
                />
              </div>
              <div className="md:col-span-2">
                <label className="label">CTA Heading</label>
                <input
                  value={homeContent.ctaHeading}
                  onChange={(e) => setHomeContent({ ...homeContent, ctaHeading: e.target.value })}
                  className="input"
                />
              </div>
              <div className="md:col-span-2">
                <label className="label">CTA Subtitle</label>
                <textarea
                  value={homeContent.ctaSubtitle}
                  onChange={(e) => setHomeContent({ ...homeContent, ctaSubtitle: e.target.value })}
                  className="input min-h-[80px]"
                />
              </div>
              <div>
                <label className="label">CTA Button Text</label>
                <input
                  value={homeContent.ctaButtonText}
                  onChange={(e) => setHomeContent({ ...homeContent, ctaButtonText: e.target.value })}
                  className="input"
                />
              </div>
              <div>
                <label className="label">CTA Button Link</label>
                <input
                  value={homeContent.ctaButtonLink}
                  onChange={(e) => setHomeContent({ ...homeContent, ctaButtonLink: e.target.value })}
                  className="input"
                />
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-stone-200 p-5 space-y-6">
            <div>
              <h4 className="font-semibold text-stone-900">About Page Content</h4>
              <p className="text-sm text-stone-500">Customize the About page hero, mission, vision, values, milestones, and call-to-action.</p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="label">Hero Title</label>
                <input
                  value={aboutContent.heroTitle}
                  onChange={(e) => setAboutContent({ ...aboutContent, heroTitle: e.target.value })}
                  className="input"
                />
              </div>
              <div className="md:col-span-2">
                <label className="label">Hero Subtitle</label>
                <textarea
                  value={aboutContent.heroSubtitle}
                  onChange={(e) => setAboutContent({ ...aboutContent, heroSubtitle: e.target.value })}
                  className="input min-h-[90px]"
                />
              </div>
              <div className="md:col-span-2">
                <label className="label">Hero Image URL</label>
                <input
                  value={aboutContent.heroImageUrl}
                  onChange={(e) => setAboutContent({ ...aboutContent, heroImageUrl: e.target.value })}
                  className="input"
                />
              </div>
              <div>
                <label className="label">Mission Title</label>
                <input
                  value={aboutContent.missionTitle}
                  onChange={(e) => setAboutContent({ ...aboutContent, missionTitle: e.target.value })}
                  className="input"
                />
              </div>
              <div>
                <label className="label">Vision Title</label>
                <input
                  value={aboutContent.visionTitle}
                  onChange={(e) => setAboutContent({ ...aboutContent, visionTitle: e.target.value })}
                  className="input"
                />
              </div>
              <div className="md:col-span-2">
                <label className="label">Mission Text</label>
                <textarea
                  value={aboutContent.missionText}
                  onChange={(e) => setAboutContent({ ...aboutContent, missionText: e.target.value })}
                  className="input min-h-[90px]"
                />
              </div>
              <div className="md:col-span-2">
                <label className="label">Vision Text</label>
                <textarea
                  value={aboutContent.visionText}
                  onChange={(e) => setAboutContent({ ...aboutContent, visionText: e.target.value })}
                  className="input min-h-[90px]"
                />
              </div>
              <div className="md:col-span-2">
                <label className="label">Values Section Heading</label>
                <input
                  value={aboutContent.valuesHeading}
                  onChange={(e) => setAboutContent({ ...aboutContent, valuesHeading: e.target.value })}
                  className="input"
                />
              </div>
              <div className="md:col-span-2">
                <label className="label">Values Section Intro</label>
                <textarea
                  value={aboutContent.valuesIntro}
                  onChange={(e) => setAboutContent({ ...aboutContent, valuesIntro: e.target.value })}
                  className="input min-h-[80px]"
                />
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h5 className="font-semibold text-stone-900">Values</h5>
                <button
                  type="button"
                  onClick={() => setAboutContent({
                    ...aboutContent,
                    values: [...aboutContent.values, { title: '', description: '' }],
                  })}
                  className="inline-flex items-center gap-2 rounded-lg border border-brand-200 px-3 py-2 text-sm text-brand-700"
                >
                  <Plus className="h-4 w-4" /> Add value
                </button>
              </div>
              {aboutContent.values.map((value, index) => (
                <div key={`${value.title}-${index}`} className="rounded-lg border border-stone-200 p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-stone-700">Value {index + 1}</span>
                    <button
                      type="button"
                      onClick={() => setAboutContent({
                        ...aboutContent,
                        values: aboutContent.values.filter((_, i) => i !== index),
                      })}
                      className="text-sm text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <input
                    value={value.title}
                    onChange={(e) => {
                      const next = [...aboutContent.values];
                      next[index] = { ...next[index], title: e.target.value };
                      setAboutContent({ ...aboutContent, values: next });
                    }}
                    placeholder="Value title"
                    className="input"
                  />
                  <textarea
                    value={value.description}
                    onChange={(e) => {
                      const next = [...aboutContent.values];
                      next[index] = { ...next[index], description: e.target.value };
                      setAboutContent({ ...aboutContent, values: next });
                    }}
                    placeholder="Value description"
                    className="input min-h-[80px]"
                  />
                </div>
              ))}
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h5 className="font-semibold text-stone-900">Milestones</h5>
                <button
                  type="button"
                  onClick={() => setAboutContent({
                    ...aboutContent,
                    milestones: [...aboutContent.milestones, { year: '', title: '', description: '' }],
                  })}
                  className="inline-flex items-center gap-2 rounded-lg border border-brand-200 px-3 py-2 text-sm text-brand-700"
                >
                  <Plus className="h-4 w-4" /> Add milestone
                </button>
              </div>
              {aboutContent.milestones.map((milestone, index) => (
                <div key={`${milestone.year}-${index}`} className="rounded-lg border border-stone-200 p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-stone-700">Milestone {index + 1}</span>
                    <button
                      type="button"
                      onClick={() => setAboutContent({
                        ...aboutContent,
                        milestones: aboutContent.milestones.filter((_, i) => i !== index),
                      })}
                      className="text-sm text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    <input
                      value={milestone.year}
                      onChange={(e) => {
                        const next = [...aboutContent.milestones];
                        next[index] = { ...next[index], year: e.target.value };
                        setAboutContent({ ...aboutContent, milestones: next });
                      }}
                      placeholder="Year"
                      className="input"
                    />
                    <input
                      value={milestone.title}
                      onChange={(e) => {
                        const next = [...aboutContent.milestones];
                        next[index] = { ...next[index], title: e.target.value };
                        setAboutContent({ ...aboutContent, milestones: next });
                      }}
                      placeholder="Title"
                      className="input"
                    />
                  </div>
                  <textarea
                    value={milestone.description}
                    onChange={(e) => {
                      const next = [...aboutContent.milestones];
                      next[index] = { ...next[index], description: e.target.value };
                      setAboutContent({ ...aboutContent, milestones: next });
                    }}
                    placeholder="Description"
                    className="input min-h-[80px]"
                  />
                </div>
              ))}
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="label">Milestones Section Heading</label>
                <input
                  value={aboutContent.milestonesHeading}
                  onChange={(e) => setAboutContent({ ...aboutContent, milestonesHeading: e.target.value })}
                  className="input"
                />
              </div>
              <div className="md:col-span-2">
                <label className="label">Milestones Section Intro</label>
                <textarea
                  value={aboutContent.milestonesIntro}
                  onChange={(e) => setAboutContent({ ...aboutContent, milestonesIntro: e.target.value })}
                  className="input min-h-[80px]"
                />
              </div>
              <div className="md:col-span-2">
                <label className="label">CTA Heading</label>
                <input
                  value={aboutContent.ctaHeading}
                  onChange={(e) => setAboutContent({ ...aboutContent, ctaHeading: e.target.value })}
                  className="input"
                />
              </div>
              <div>
                <label className="label">CTA Button Text</label>
                <input
                  value={aboutContent.ctaButtonText}
                  onChange={(e) => setAboutContent({ ...aboutContent, ctaButtonText: e.target.value })}
                  className="input"
                />
              </div>
              <div>
                <label className="label">CTA Link</label>
                <input
                  value={aboutContent.ctaLink}
                  onChange={(e) => setAboutContent({ ...aboutContent, ctaLink: e.target.value })}
                  className="input"
                />
              </div>
            </div>
          </div>
        </div>

        <button type="submit" disabled={saving} className="btn-primary">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save Settings
        </button>
      </form>
    </div>
  );
}
